import React from 'react';
import { api, fmt } from '../api';
import { Empty, ErrorBanner, Field, Hero, Loading, Pill, SmsPreview, statusTone, StepProgress, timezoneSelectOptions, TrustStrip, useAsync } from '../ui';
import { navigate, type Me } from '../App';
import { HomeFamilyIllustration, SeedlingIllustration, ShieldHeartIllustration } from '../illustrations';
import { EarlyReleaseNotice } from './EarlyRelease';
import { useDraftProtection } from '../navigation';
import { familyPrimaryAction, familyStudentNextStep, hasFamilyPermissions } from '../student-journey';
import { TutorialVideo } from '../TutorialVideo';

const CONSENT_LABELS: Record<string, string> = {
  guardian_attestation: 'I am the parent or legal guardian of the child I am adding.',
  student_data: 'I authorize this service to store my child\'s school work and schedule data.',
  sms_messaging: 'I authorize text messages to the number I verify, and I understand message and data rates may apply.',
  ai_processing: 'I authorize AI interpretation of my child\'s replies and summaries of their schoolwork.',
  voice_processing: 'I authorize voice conversations with the assistant when voice is available, and understand that voice audio is processed during the active session.',
  school_connection: 'I authorize a read-only connection to my child\'s school system after my child signs in.',
  early_release_disclaimer: 'I have read and understand the Early Release Notice above. I will supervise my child’s use, verify important information, and not rely on this service for emergencies.',
};

const SMS_APPROVAL_BEHAVIOR = {
  systemBehavior: 'Live SMS reminders and student replies',
  quietHours: 'Scheduled messages use each child\'s quiet hours and school-hour suppression settings',
  optOut: 'SMS STOP unsubscribes; START can resubscribe when eligible; parent pause/resume uses Command Center controls',
};

function ConsentGate({ me, onDone, familyHome = false }: {
  me: Me;
  onDone: () => void | Promise<void>;
  familyHome?: boolean;
}) {
  const required = me.policyVersionRequired ?? [];
  const familyConsents = (me.consents ?? []).filter((consent) => consent.student_id == null);
  const missing = required.filter((type) => !familyConsents.some((consent) =>
    consent.type === type &&
    consent.policy_version === (type === 'early_release_disclaimer'
      ? me.earlyReleaseDisclaimerVersion
      : me.policyVersion)));
  const [checked, setChecked] = React.useState<Record<string, boolean>>({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!missing.length) return (
    <section className="card" id="family-permissions" tabIndex={-1}>
      <h2>Recorded family permissions</h2>
      <p className="muted">These permissions cover your family, not just one child. All required permissions are current.</p>
      <ul className="plain">
        {familyConsents.map((consent) => (
          <li key={`${consent.type}:${consent.policy_version}:${consent.granted_at}`}>
            <strong>{CONSENT_LABELS[consent.type] ?? consent.type}</strong>
            <div className="muted">Recorded {fmt.dateTime(consent.granted_at)} · Policy {consent.policy_version}</div>
          </li>
        ))}
      </ul>
    </section>
  );

  const all = missing.every((t) => checked[t]);
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      for (const type of missing) {
        await api('POST', '/api/consents', {
          type,
          attestation: type === 'early_release_disclaimer'
            ? {
              accepted: true,
              disclaimerVersion: me.earlyReleaseDisclaimerVersion,
              acceptedText: CONSENT_LABELS[type],
              source: 'parent_onboarding',
            }
            : { acceptedText: CONSENT_LABELS[type] },
        });
      }
      await onDone();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={familyHome ? 'card family-home-permissions' : 'card'} id="family-permissions" tabIndex={-1}>
      {!familyHome && <StepProgress
        steps={['Permissions', 'Child profile', 'Connect school', 'Verify phone', 'Activate assistant']}
        currentIndex={0}
      />}
      <div className="trust-panel-header">
        <span className="illustration-wrap" aria-hidden="true"><ShieldHeartIllustration title="Trust" /></span>
        <div>
          <h2>{familyHome ? 'Review permissions' : 'Step 1: Permissions'}</h2>
          <p className="muted" style={{ margin: 0 }}>
            These permissions allow Student Assistants to store school data, send SMS, use AI interpretation, and—when enabled—support voice conversations. Your answers are recorded with a timestamp.
          </p>
        </div>
      </div>
      <p className="banner" style={{ marginTop: 12 }}>
        <strong>Important:</strong> These checkboxes are separate from phone verification. They authorize how the assistant may use data and messages; they do not verify a student phone number.
      </p>
      <ul className="plain trust-panel-list">
        {missing.map((t) => (
          <li key={t}>
            <label className="consent-row">
              <input
                type="checkbox"
                checked={!!checked[t]}
                onChange={(e) => setChecked({ ...checked, [t]: e.target.checked })}
              />
              <span>{CONSENT_LABELS[t] ?? t}</span>
            </label>
          </li>
        ))}
      </ul>
      {error && <p className="banner bad" role="alert">{error}</p>}
      <button className="primary" disabled={!all || busy} onClick={submit}>
        {busy ? 'Recording…' : familyHome ? 'Save permissions and continue' : 'Record consent'}
      </button>
    </section>
  );
}

function ParentApprovalGate({ familyData, onDone }: { familyData: any; onDone: () => void | Promise<void> }) {
  const [checked, setChecked] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (familyData.parentApproval?.approved) return null;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api('POST', '/api/parent-approval', {
        attestation: {
          acceptedText: 'I understand and approve live SMS activation to my family.',
          ...SMS_APPROVAL_BEHAVIOR,
        },
      });
      await onDone();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="trust-panel-header">
        <span className="illustration-wrap" aria-hidden="true"><ShieldHeartIllustration title="Trust" /></span>
        <div>
          <h2>Live SMS activation approval</h2>
          <p className="muted" style={{ margin: 0 }}>
            Before live text messages can be sent to your family's students, you must review and approve the SMS behavior below.
          </p>
        </div>
      </div>

      <div className="trust-panel-list sms-approval-summary">
        <strong>What this means:</strong>
        <ul>
          <li>✓ Live text reminders can be sent only to verified, consented student phone numbers.</li>
          <li>✓ Students can reply by text; replies may be processed outside scheduled reminder quiet hours so the assistant can answer.</li>
          <li>✓ Scheduled outbound reminders respect each child's quiet hours and school-hour suppression settings before dispatch.</li>
          <li>✓ Daily outbound limits use each child's Daily message limit setting; scheduled reminders and accepted/delivered responses count toward that daily cap.</li>
          <li>✓ SMS STOP unsubscribes that recipient; START can resubscribe when eligible. Parent pause/resume uses Command Center controls.</li>
          <li>✓ Delivery status is tracked. Message-content visibility follows each child's parent-visibility setting.</li>
        </ul>
        <p className="settings-note">
          <strong>Reminder settings can be changed later.</strong> After onboarding, open your child's <strong>Configuration & tools → Assistant settings</strong> to adjust quiet hours, reminder times, daily message limits, and parent message visibility. Settings apply separately to each child.
        </p>
      </div>

      <label className="consent-row sms-approval-row">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
        <span>I have reviewed the SMS behavior above and approve live SMS activation for my family.</span>
      </label>

      {error && <p className="banner bad" role="alert">{error}</p>}
      <div style={{ marginTop: 12 }}>
        <button className="primary" disabled={!checked || busy} onClick={submit}>
          {busy ? 'Recording approval…' : 'Approve Live SMS'}
        </button>
      </div>
    </section>
  );
}

function SmsApprovalStatus({ familyData }: { familyData: any }) {
  const approvedAt = familyData.parentApproval?.approvalDate
    ? new Date(familyData.parentApproval.approvalDate).toLocaleDateString()
    : 'Recorded';
  return (
    <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
      <span className="muted">
        SMS approval: <strong>Approved</strong> · {approvedAt} · Delivery still requires an active assistant and a verified, opted-in phone
      </span>
      <Pill tone="ok">Family-level approval</Pill>
    </div>
  );
}

export function FamilyPermissions({
  me,
  onChanged,
  onReady,
}: {
  me: Me;
  onChanged: () => void | Promise<void>;
  onReady?: () => void;
}) {
  const family = useAsync(() => api('GET', '/api/family'), []);
  const consent = (me.consents ?? []).find((c) => c.student_id == null && c.type === 'early_release_disclaimer' && c.policy_version === me.earlyReleaseDisclaimerVersion);
  const consentDone = async () => {
    await onChanged();
    family.reload();
    onReady?.();
  };
  return <>
    <EarlyReleaseNotice version={me.earlyReleaseDisclaimerVersion ?? 'current'} acceptedAt={consent?.granted_at}
      acceptedVersion={consent?.policy_version} supportEmail={me.supportEmail} />
    <ConsentGate me={me} onDone={consentDone} />
    {family.error && <ErrorBanner error={family.error} onRetry={family.reload} />}
    {family.data && hasFamilyPermissions(me) && (family.data.parentApproval?.approved
      ? <section className="card"><h2>Recorded live SMS approval</h2><SmsApprovalStatus familyData={family.data} /></section>
      : <ParentApprovalGate familyData={family.data} onDone={consentDone} />)}
  </>;
}

function AddStudent({
  onAdded,
  permissionsReady,
  isFirstChild,
}: {
  onAdded: () => void;
  permissionsReady: boolean;
  isFirstChild: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({
    preferredName: '', studentMobile: '', studentEmailAlias: '', schoolName: '', grade: '7',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
  });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const clearDraft = useDraftProtection(open && Boolean(f.preferredName || f.studentMobile || f.studentEmailAlias || f.schoolName));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const created = await api('POST', '/api/students', {
        preferredName: f.preferredName,
        studentMobile: f.studentMobile,
        studentEmailAlias: f.studentEmailAlias || undefined,
        schoolName: f.schoolName.trim(),
        grade: Number(f.grade),
        timezone: f.timezone,
      });
      setOpen(false);
      setF({ ...f, preferredName: '', studentMobile: '', studentEmailAlias: '', schoolName: '' });
      onAdded();
      clearDraft();
      if (created?.studentId) navigate(`/students/${created.studentId}?setup=continue`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return (
    <div>
      <button id="family-add-child-action" className="primary" disabled={!permissionsReady} onClick={() => setOpen(true)}>
        {isFirstChild ? 'Add your first child' : 'Add another child'}
      </button>
      {!permissionsReady && <p className="field-help" style={{ marginBottom: 0 }}>Complete Step 1: Permissions before adding a child.</p>}
    </div>
  );

  return (
    <form className="card" onSubmit={submit}>
      <h2>{isFirstChild ? 'Add your first child' : 'Add another child'}</h2>
      <p className="muted">
        Create this child’s profile first. The assistant will stay inactive until Canvas is connected, its first sync succeeds, and the student’s phone is verified.
      </p>
      <StepProgress
        steps={['Permissions', 'Child profile', 'Connect school', 'Verify phone', 'Activate assistant']}
        currentIndex={1}
      />
      <div className="banner">
        <strong>Before you start:</strong> have the student’s phone or watch nearby for verification and their Canvas sign-in available for the next step.
      </div>

      <fieldset>
        <legend>Step 1: Child profile</legend>
        <div className="grid">
          <Field label="Preferred name (required)" value={f.preferredName} onChange={(v) => setF({ ...f, preferredName: v })} required />
          <Field label="Grade (required)" value={f.grade} onChange={(v) => setF({ ...f, grade: v })}
            options={[5, 6, 7, 8, 9, 10, 11, 12].map((g) => ({ value: String(g), label: `Grade ${g}` }))} />
          <div className="field-span">
            <Field label="Current school name (required)" value={f.schoolName}
              onChange={(v) => setF({ ...f, schoolName: v })} placeholder="e.g. The Bush School" required />
            <p className="field-help">
              Use the student’s current school name. It helps the assistant match sports schedules, game reminders,
              and school-specific setup later.
            </p>
          </div>
          <div className="field-span">
            <Field label="Student phone number (required)" type="tel" value={f.studentMobile}
              onChange={(v) => setF({ ...f, studentMobile: v })} placeholder="(425) 555-0123" required />
            <p className="field-help">This is the phone or watch number the student carries. We send a code before reminders can go there.</p>
          </div>
          <Field label="Timezone (required)" value={f.timezone} onChange={(v) => setF({ ...f, timezone: v })} required
            options={timezoneSelectOptions(f.timezone)} />
        </div>
      </fieldset>

      <details className="optional-profile-details">
        <summary>Optional: customize the email-forwarding name</summary>
        <div className="field-span">
          <Field label="Assistant email name" value={f.studentEmailAlias}
            onChange={(v) => setF({ ...f, studentEmailAlias: v })} placeholder="ruhi1" />
          <p className="field-help">Leave blank to generate a unique forwarding address. You can set this up later.</p>
        </div>
      </details>
      <details className="optional-profile-details">
        <summary>See a sample student text</summary>
        <SmsPreview caption="A sample reminder this child could receive" />
      </details>
      {error && <p className="banner bad" role="alert">{error}</p>}
      <div className="row" style={{ marginTop: 12 }}>
        <button className="primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create child profile'}</button>
        <button type="button" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

function scrollAndFocus(targetId: string) {
  const target = document.getElementById(targetId);
  target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  target?.focus({ preventScroll: true });
}

function StudentCard({ s, isParent }: { s: any; isParent: boolean }) {
  const blockers: string[] = s.readiness?.blockers ?? [];
  const nextStep = familyStudentNextStep(blockers);
  const phoneRepairNeeded = blockers.some((blocker) => /sms destination|phone/i.test(blocker));
  const schoolRepairNeeded = blockers.some((blocker) => /canvas|assignment source/i.test(blocker));
  const onboarding = s.worker_status === 'setup_required' && !s.activated_at;
  const initial = (s.preferred_name || '?').trim().charAt(0).toUpperCase();
  const dashboardTarget = onboarding
    ? `/students/${s.id}?setup=continue`
    : phoneRepairNeeded
      ? `/students/${s.id}?tab=phone#phone-verification-section`
      : schoolRepairNeeded
        ? `/students/${s.id}?tab=school`
        : `/students/${s.id}`;
  return (
    <article className="card interactive">
      <div className="spread">
        <div className="row" style={{ gap: 12 }}>
          <span className="child-avatar" aria-hidden="true">{initial}</span>
          <div>
            <h2 style={{ marginBottom: 2 }}>{s.preferred_name}</h2>
            <div className="muted">Student Assistant</div>
          </div>
        </div>
        <div className="row">
          <Pill tone={statusTone(s.worker_status)}>{fmt.label(s.worker_status)}</Pill>
          <Pill tone={s.readiness?.assignmentSource?.ready ? 'ok' : 'warn'}>
            {s.readiness?.assignmentSource?.ready ? 'Canvas ready' : 'Canvas needed'}
          </Pill>
        </div>
      </div>

      {nextStep && (
        <div className="next-step-callout">
          <strong>{onboarding ? 'Next setup step' : 'Repair needed before activation or resume'}</strong>
          {nextStep}
        </div>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button className="card-action" type="button" onClick={() => navigate(dashboardTarget)}>
          {isParent && onboarding
            ? 'Continue setup →'
            : phoneRepairNeeded
              ? 'Verify student phone →'
              : `Open ${s.preferred_name}'s dashboard →`}
        </button>
      </div>
      {isParent && (
        <div className="banner" style={{ marginTop: 12 }}>
          <strong>Nothing to do now for phone calls</strong>
          <p className="field-help" style={{ margin: '4px 0 0' }}>
            If you start a call with {s.preferred_name}, keep the verified phone nearby. We text a one-time code when
            the call begins; enter it when prompted. There is no permanent PIN to remember.
          </p>
        </div>
      )}
    </article>
  );
}

export function FamilyView({ me, onChanged }: { me: Me; onChanged: () => void | Promise<void> }) {
  const { data, error, loading, reload } = useAsync(() => api('GET', '/api/family'), []);
  const [focusAfterConsent, setFocusAfterConsent] = React.useState(false);

  // Short polling keeps application state fresh without a socket layer.
  React.useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  const isParent = me.principal?.role === 'parent';
  const permissionsReady = hasFamilyPermissions(me);
  const childCount = data?.students?.length ?? 0;
  const firstChildSetup = isParent && childCount === 0;
  const primaryAction = familyPrimaryAction(permissionsReady, childCount);
  const releaseConsent = (me.consents ?? []).find((consent) =>
    consent.student_id == null && consent.type === 'early_release_disclaimer' &&
    consent.policy_version === me.earlyReleaseDisclaimerVersion);
  const pendingStudent = (data?.students ?? []).find((student: any) =>
    student.worker_status === 'setup_required' && !student.activated_at);
  const familySetupStage = !permissionsReady ? 0 : !childCount ? 1 : pendingStudent ? 2 : 3;
  const familySetupMessage = familySetupStage === 0
    ? childCount
      ? 'Your family permissions need review. Your children are still listed below; review the required permissions before adding a child or continuing activation.'
      : 'First review the Early Release Notice and choose each required permission. Then you can add your first child.'
    : familySetupStage === 1
      ? 'Add your first child profile below. Their school, phone, and activation steps follow in guided setup.'
      : familySetupStage === 2
        ? `${childCount} child profile${childCount === 1 ? '' : 's'} added. Continue ${pendingStudent.preferred_name}'s guided setup.`
      : 'Setup is complete. Manage a child’s assistant below or add another child here.';
  const consentDone = async () => {
    setFocusAfterConsent(true);
    await onChanged();
    reload();
  };

  React.useEffect(() => {
    if (!focusAfterConsent || loading || !data || !permissionsReady) return;
    const target = pendingStudent
      ? document.getElementById('family-continue-setup-action')
      : document.getElementById('family-add-child-action');
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: 'auto', block: 'center' });
      target.focus({ preventScroll: true });
      setFocusAfterConsent(false);
    }
  }, [data, focusAfterConsent, loading, pendingStudent, permissionsReady]);

  if (loading && !data) return <Loading what="your family" />;
  if (error) return <ErrorBanner error={error} onRetry={reload} />;

  return (
    <>
      {firstChildSetup ? (
        <header className="family-welcome">
          <p className="eyebrow">Family Command Center</p>
          <h1>Welcome to Family Home</h1>
          <p>{permissionsReady
            ? 'Your parent account and family permissions are ready. Next, add your first child.'
            : 'Your parent account is ready. First review permissions, then add a child.'}</p>
        </header>
      ) : <Hero
        eyebrow="Family Command Center"
        title={isParent ? 'Your family' : 'Your Student Assistant'}
        subtitle={isParent
          ? childCount
            ? 'A calm, organized view of every child\'s assistant — reminders, schoolwork, and messages, all in one place.'
            : 'Your parent account is ready. Add a child profile to begin guided Student Assistant setup.'
          : 'Everything your assistant is helping you keep track of, in one place.'}
        illustration={<HomeFamilyIllustration title="A family at home together" decorative={false} />}
        stats={isParent ? [`${childCount} child profile${childCount === 1 ? '' : 's'}`] : undefined}
      />}

      {isParent && (
        <section className="card family-setup-progress" aria-labelledby="family-setup-progress-title">
          <div className="spread">
            <div>
              <h2 id="family-setup-progress-title">Family setup</h2>
              {firstChildSetup && permissionsReady && <p className="family-permissions-saved" role="status">Family permissions saved and up to date.</p>}
              <p className="muted family-setup-progress-message">{familySetupMessage}</p>
            </div>
            {((familySetupStage === 0 && !firstChildSetup) || familySetupStage === 2) && (
              <button
                className={familySetupStage === 0 ? 'primary family-setup-progress-action' : 'family-setup-progress-action'}
                id={familySetupStage === 2 ? 'family-continue-setup-action' : undefined}
                type="button"
                onClick={() => {
                  if (familySetupStage === 0) {
                    scrollAndFocus(releaseConsent ? 'family-permissions' : 'early-release-title');
                  } else if (pendingStudent) {
                    navigate(`/students/${pendingStudent.id}?setup=continue`);
                  }
                }}
              >
                {familySetupStage === 0
                  ? releaseConsent ? 'Review required permissions' : 'Review notice and permissions'
                  : `Continue ${pendingStudent.preferred_name}'s setup`}
              </button>
            )}
          </div>
          {firstChildSetup && !permissionsReady && (
            <div className="family-permissions-task">
              <EarlyReleaseNotice
                version={me.earlyReleaseDisclaimerVersion ?? 'current'}
                acceptedAt={releaseConsent?.granted_at}
                acceptedVersion={releaseConsent?.policy_version}
                supportEmail={me.supportEmail}
              />
              <ConsentGate me={me} onDone={consentDone} familyHome />
            </div>
          )}
          {permissionsReady && !data.parentApproval?.approved && (
            <p className="muted family-setup-sms-note">
              Live SMS approval is separate from child setup; it controls when real text messages may be sent.
            </p>
          )}
          {primaryAction !== 'review_permissions' && (
            <div className="family-setup-add-child">
              <AddStudent
                onAdded={reload}
                permissionsReady={permissionsReady}
                isFirstChild={primaryAction === 'add_first_child'}
              />
              <TutorialVideo
                title="Add a child profile"
                description="See the Family home action and the next guided-setup step. Example child and phone details are fictional."
                videoSrc="/tutorials/add-child.mp4"
                posterSrc="/tutorials/add-child-poster.jpg"
                captionsSrc="/tutorials/add-child.vtt"
              />
            </div>
          )}
        </section>
      )}

      {isParent && (
        <TrustStrip illustration={<ShieldHeartIllustration title="Privacy" decorative={false} />}>
          You control what each child's assistant can see and do. Family permissions cover all children; phone, visibility and reminder settings are child-specific.
        </TrustStrip>
      )}

      {!firstChildSetup && <section className="card" id={isParent ? 'family-children' : undefined}>
        <div className="spread">
          <div>
            <h2 id={isParent ? 'family-children-title' : undefined} tabIndex={isParent ? -1 : undefined}>
              {isParent ? 'Children' : 'Your Student Assistant'}
            </h2>
            <div className="muted">
              {childCount
                ? 'Open a child’s Student Assistant to view work, reminders, messages, and settings.'
                : 'Add a child profile first; school, phone, and activation steps follow in guided setup.'}
            </div>
          </div>
        </div>
        {isParent && data.parentApproval?.approved && <SmsApprovalStatus familyData={data} />}
      </section>}

      {!firstChildSetup && (data.students?.length
        ? data.students.map((s: any) => <StudentCard key={s.id} s={s} isParent={isParent} />)
        : (
          <div className="card">
            <Empty
              illustration={<SeedlingIllustration title="A small seedling, just getting started" decorative={false} />}
              action={isParent ? <span className="muted">Use “Add your first child” above to begin. The student phone is required there and verified before reminders can be sent.</span> : undefined}
            >
              Your parent account is ready; no child profile or Student Assistant has been created yet.
            </Empty>
          </div>
        ))}

      {isParent && (!firstChildSetup || permissionsReady) && (
        <div id="family-permissions-review">
          {!permissionsReady && (
            <EarlyReleaseNotice
              version={me.earlyReleaseDisclaimerVersion ?? 'current'}
              acceptedAt={releaseConsent?.granted_at}
              acceptedVersion={releaseConsent?.policy_version}
              supportEmail={me.supportEmail}
            />
          )}
          {!permissionsReady && <ConsentGate me={me} onDone={consentDone} familyHome />}
          {permissionsReady && !data.parentApproval?.approved && (
            <ParentApprovalGate familyData={data} onDone={consentDone} />
          )}
          {permissionsReady && releaseConsent && (
            <details className="card family-history-disclosure">
              <summary>
                Early Release Notice
                <span className="muted">Acknowledged {fmt.date(releaseConsent.granted_at)}</span>
              </summary>
              <EarlyReleaseNotice
                version={me.earlyReleaseDisclaimerVersion ?? 'current'}
                acceptedAt={releaseConsent.granted_at}
                acceptedVersion={releaseConsent.policy_version}
                supportEmail={me.supportEmail}
              />
            </details>
          )}
          {permissionsReady && <ConsentGate me={me} onDone={consentDone} familyHome />}
        </div>
      )}
    </>
  );
}
